import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useRef, useState } from 'react';
import { Camera, CheckCircle2, ChevronDown, CircleAlert, LoaderCircle, Smartphone, X } from 'lucide-react';
import api from '../../utils/api';
import { uploadProfileImage } from '../../utils/profileUploadApi';
import UserAvatar from '../components/UserAvatar';
import RegionCascader from './RegionCascader';
import { useTeamBrand } from '../branding/TeamBrandContext';

const ACCEPTED_AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_AVATAR_SIZE = 2 * 1024 * 1024;

function buildInitialForm(user) {
  return {
    username: user?.username || '',
    avatarUrl: user?.avatar_url || '',
    bio: user?.bio || '',
    profession: user?.profession || '',
    regionId: user?.region_id === null || user?.region_id === undefined ? '' : String(user.region_id),
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('读取图片失败'));
    reader.readAsDataURL(file);
  });
}

function messageFromError(error, fallback) {
  return getErrorMessage(error, fallback);
}

export default function ProfileSettingsPanel({
  user,
  refreshUser,
  preview = false,
  english = false,
}) {
  const brand = useTeamBrand();
  const initialForm = buildInitialForm(user);
  const [form, setForm] = useState(initialForm);
  const [savedForm, setSavedForm] = useState(initialForm);
  const dirtyRef = useRef(false);
  const fileInputRef = useRef(null);
  const [regions, setRegions] = useState([]);
  const [regionLoading, setRegionLoading] = useState(false);
  const [regionError, setRegionError] = useState('');
  const [regionReloadKey, setRegionReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const dirty = JSON.stringify(form) !== JSON.stringify(savedForm);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (dirtyRef.current) return;
    const nextForm = buildInitialForm(user);
    setForm(nextForm);
    setSavedForm(nextForm);
  }, [user]);

  useEffect(() => {
    if (preview) {
      setRegions([]);
      return undefined;
    }
    let active = true;
    setRegionLoading(true);
    setRegionError('');
    api.get(`/regions?refresh=${regionReloadKey}&_=${Date.now()}`)
      .then(response => {
        if (!active) return;
        const items = Array.isArray(response.data) ? response.data : response.data?.items;
        setRegions(Array.isArray(items) ? items : []);
      })
      .catch(error => {
        if (!active) return;
        setRegions([]);
        setRegionError(getErrorMessage(messageFromError(error, '地区加载失败')));
      })
      .finally(() => {
        if (active) setRegionLoading(false);
      });
    return () => { active = false; };
  }, [english, preview, regionReloadKey]);

  const updateField = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    setFeedback({ type: '', message: '' });
  };

  const handleAvatarChange = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ACCEPTED_AVATAR_TYPES.has(file.type)) {
      setFeedback({ type: 'error', message: '请选择 JPG、PNG 或 WebP 图片' });
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      setFeedback({ type: 'error', message: '头像大小不能超过 2 MB' });
      return;
    }

    setUploading(true);
    setFeedback({ type: '', message: '' });
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const avatarUrl = await uploadProfileImage(dataUrl);
      setForm(current => ({ ...current, avatarUrl }));
      setFeedback({ type: 'success', message: english ? 'Avatar uploaded. Save your profile to apply it.' : '头像已上传，保存资料后生效' });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(messageFromError(error, '头像上传失败')) });
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async () => {
    if (preview || !dirty || saving || uploading) return;
    setSaving(true);
    setFeedback({ type: '', message: '' });
    try {
      const response = await api.patch('/users/me', {
        username: form.username,
        avatar_url: form.avatarUrl || null,
        bio: form.bio || null,
        profession: form.profession || null,
        region_id: form.regionId || null,
      });
      const nextForm = buildInitialForm(response.data);
      dirtyRef.current = false;
      setForm(nextForm);
      setSavedForm(nextForm);
      setFeedback({ type: 'success', message: english ? 'Profile saved.' : '个人资料已保存' });
      try {
        await refreshUser?.();
      } catch {
        setFeedback({
          type: 'warning',
          message: '个人资料已保存，但全局资料刷新失败，请刷新页面确认',
        });
      }
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(messageFromError(error, '保存个人资料失败')) });
    } finally {
      setSaving(false);
    }
  };

  const initial = form.username?.trim()?.[0]?.toUpperCase() || (english ? 'X' : '影');
  const avatarUser = { ...user, username: form.username, avatar_url: form.avatarUrl };

  return (
    <>
      <section className="account-page-heading" data-account-unsaved={!preview && dirty ? 'true' : undefined} data-account-busy={saving || uploading ? 'true' : undefined}>
        <div>
          <span className="account-eyebrow">PERSONAL PROFILE</span>
          <h1>{english ? 'Profile' : '个人资料'}</h1>
          <p>{brand.text(english ? 'These details identify you across Xiaoying workspaces.' : '这些资料用于小影工作区中的身份展示。')}</p>
        </div>
      </section>

      {feedback.message && (
        <div
          className={`account-profile-toast account-profile-toast--${feedback.type}`}
          role={feedback.type === 'error' ? 'alert' : 'status'}
          aria-live={feedback.type === 'error' ? 'assertive' : 'polite'}
        >
          {feedback.type === 'success'
            ? <CheckCircle2 size={17} />
            : <CircleAlert size={17} />}
          <span>{feedback.message}</span>
          <button
            type="button"
            aria-label={english ? 'Dismiss notification' : '关闭提示'}
            onClick={() => setFeedback({ type: '', message: '' })}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <section className="account-settings-card account-profile-settings">
        <div className="account-profile-settings__identity">
          <UserAvatar user={avatarUser} fallback={initial} className="account-profile-settings__avatar">
            <button
              type="button"
              aria-label={english ? 'Change avatar' : '更换头像'}
              disabled={preview || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? <LoaderCircle className="account-spin" size={14} /> : <Camera size={14} />}
            </button>
          </UserAvatar>
          <div>
            <h2>{english ? 'Avatar & identity' : '头像与身份'}</h2>
            <p>{english ? 'JPG, PNG, or WebP; up to 2 MB.' : '支持 JPG、PNG、WebP，最大 2 MB。'}</p>
          </div>
          <button
            className="account-button"
            type="button"
            disabled={preview || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (english ? 'Uploading…' : '上传中…') : (english ? 'Change avatar' : '更换头像')}
          </button>
          <input
            ref={fileInputRef}
            className="account-profile-settings__file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label={english ? 'Upload avatar' : '上传头像'}
            disabled={preview || uploading}
            onChange={handleAvatarChange}
          />
        </div>

        <div className="account-profile-form">
          <label className="account-field account-field--wide">
            <span>{english ? 'Display name' : '昵称'}</span>
            <input
              name="username"
              value={form.username}
              onChange={updateField}
              maxLength={32}
              disabled={preview}
              placeholder={english ? 'Enter your creator name' : '输入你的创作者昵称'}
            />
            <small>{form.username.length}/32</small>
          </label>

          <label className="account-field account-field--wide">
            <span>{english ? 'Bio' : '个人简介'}</span>
            <textarea
              name="bio"
              value={form.bio}
              onChange={updateField}
              maxLength={160}
              rows={4}
              disabled={preview}
              placeholder={english ? 'Describe your creative focus, favorite genres, or current project' : '简单介绍你的创作方向、擅长题材或当前项目'}
            />
            <small>{form.bio.length}/160</small>
          </label>

          <label className="account-field">
            <span>{english ? 'Role / Profession' : '身份 / 职业'}</span>
            <div className="account-field__select">
              <select name="profession" value={form.profession} onChange={updateField} disabled={preview}>
                <option value="">{english ? 'Select' : '请选择'}</option>
                <option value="screenwriter">{english ? 'Screenwriter' : '编剧'}</option>
                <option value="director">{english ? 'Director' : '导演'}</option>
                <option value="producer">{english ? 'Producer' : '制片人'}</option>
                <option value="creator">{english ? 'Content creator' : '内容创作者'}</option>
                <option value="studio">{english ? 'Short-drama studio' : '短剧工作室'}</option>
                <option value="other">{english ? 'Other' : '其他'}</option>
              </select>
              <ChevronDown size={14} />
            </div>
          </label>

          <label className="account-field">
            <span>{english ? 'Country / Region' : '国家 / 地区'}</span>
            <RegionCascader
              items={regions}
              value={form.regionId}
              onChange={regionId => {
                setForm(current => ({ ...current, regionId }));
                setFeedback({ type: '', message: '' });
              }}
              disabled={preview}
              loading={regionLoading}
              error={regionError}
              onRetry={() => setRegionReloadKey(key => key + 1)}
              english={english}
            />
          </label>
        </div>

        <div className="account-profile-settings__footer">
          <div>
            <Smartphone size={14} />
            <span>{english ? 'Manage WeChat and phone binding under Sign-in & Security.' : '微信与手机号绑定在“登录与安全”中管理'}</span>
          </div>
          <div className="account-profile-settings__actions">
            <button
              className="account-button account-button--disabled"
              type="button"
              disabled={preview || !dirty || saving || uploading}
              onClick={saveProfile}
            >
              {saving ? <LoaderCircle className="account-spin" size={14} /> : null}
              {saving ? (english ? 'Saving…' : '保存中…') : (english ? 'Save profile' : '保存资料')}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
